import { useRef, useState, useEffect } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Activity, Wifi, Bell, Hand, Brain, Lightbulb, Cog } from "lucide-react";
import { LOGO } from "../lib/assets";

// --- projective (homography) helpers: map a source rect to any 4-corner quad ---
function adj(m) {
  return [
    m[4] * m[8] - m[5] * m[7], m[2] * m[7] - m[1] * m[8], m[1] * m[5] - m[2] * m[4],
    m[5] * m[6] - m[3] * m[8], m[0] * m[8] - m[2] * m[6], m[2] * m[3] - m[0] * m[5],
    m[3] * m[7] - m[4] * m[6], m[1] * m[6] - m[0] * m[7], m[0] * m[4] - m[1] * m[3],
  ];
}
function multmm(a, b) {
  const c = [];
  for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) {
    let s = 0;
    for (let k = 0; k < 3; k++) s += a[3 * i + k] * b[3 * k + j];
    c[3 * i + j] = s;
  }
  return c;
}
function multmv(m, v) {
  return [
    m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
    m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
    m[6] * v[0] + m[7] * v[1] + m[8] * v[2],
  ];
}
function basisToPoints(x1, y1, x2, y2, x3, y3, x4, y4) {
  const m = [x1, x2, x3, y1, y2, y3, 1, 1, 1];
  const v = multmv(adj(m), [x4, y4, 1]);
  return multmm(m, [v[0], 0, 0, 0, v[1], 0, 0, 0, v[2]]);
}
// src corners TL,TR,BL,BR of a w×h rect → dst corners TL,TR,BL,BR in px
function matrix3dFor(w, h, dst) {
  const s = basisToPoints(0, 0, w, 0, 0, h, w, h);
  const d = basisToPoints(dst[0][0], dst[0][1], dst[1][0], dst[1][1], dst[2][0], dst[2][1], dst[3][0], dst[3][1]);
  let t = multmm(d, adj(s));
  for (let i = 0; i < 9; i++) t[i] = t[i] / t[8];
  const m = [t[0], t[3], 0, t[6], t[1], t[4], 0, t[7], 0, 0, 1, 0, t[2], t[5], 0, t[8]];
  return "matrix3d(" + m.join(",") + ")";
}


const A = "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/";

export function PhoneScreen({ c = "#e6c068", active = true }) {
  const [mins, setMins] = useState(22);
  const [bpm, setBpm] = useState(118);
  const [flashDispense, setFlashDispense] = useState(false);
  useEffect(() => {
    // every reveal starts on a full ring: reset the minute count AND restart
    // the interval's phase together, so the first tick doesn't fire early
    if (active) setMins(22);
    const id = setInterval(() => {
      setMins((m) => {
        if (m <= 0) {
          setFlashDispense(true);
          setTimeout(() => setFlashDispense(false), 1300);
          return 22;
        }
        return m - 1;
      });
    }, 480);
    return () => clearInterval(id);
  }, [active]);
  useEffect(() => {
    const hr = setInterval(() => setBpm(112 + Math.floor(Math.random() * 12)), 1100);
    return () => clearInterval(hr);
  }, []);
  const pct = Math.round((mins / 22) * 100);

  return (
    <div
      className="relative w-full h-full rounded-[16px] overflow-hidden"
      style={{
        background: "linear-gradient(180deg,#141018 0%,#0b0a0e 100%)",
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
        padding: "5% 6%",
        fontSize: "clamp(12px, 1.6vw, 19px)",
      }}
    >
      {/* boot sequence: logo zings on with an electric flicker, then holds in a fluid
          breathing glow until the signal arc lands and the full UI takes over */}
      <AnimatePresence>
        {!active && (
          <motion.div
            className="absolute inset-0 z-20 flex items-center justify-center"
            exit={{ opacity: 0, scale: 1.2 }}
            transition={{ duration: 0.4, ease: "easeIn" }}
          >
            <div className="relative flex items-center justify-center" style={{ width: "36%", aspectRatio: 1 }}>
              {[0, 1, 2].map((i) => (
                <motion.span
                  key={i}
                  className="absolute inset-0 rounded-full"
                  style={{ border: "1.5px solid #4fd6ff" }}
                  initial={{ opacity: 0, scale: 0.55 }}
                  animate={{ opacity: [0, 0.85, 0], scale: [0.55, 1.5, 1.9] }}
                  transition={{ duration: 1.3, delay: 0.18 + i * 0.22, repeat: Infinity, repeatDelay: 0.55, ease: "easeOut" }}
                />
              ))}
              <motion.img
                src={LOGO}
                alt=""
                aria-hidden="true"
                className="relative w-full h-full object-contain"
                style={{ filter: "drop-shadow(0 0 14px rgba(79,214,255,0.85))" }}
                initial={{ opacity: 0, scale: 0.5, rotate: -10 }}
                animate={{
                  opacity: [0, 1, 0.45, 1, 0.55, 1, 0.9, 1],
                  scale: [0.5, 1.1, 0.95, 1.05, 0.98, 1.03, 1],
                  rotate: [-10, 4, -3, 1.5, 0],
                }}
                transition={{ duration: 0.65, times: [0, 0.14, 0.24, 0.36, 0.48, 0.62, 0.8, 1], ease: "easeOut" }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* one-shot light-up flash the instant the signal arrives */}
      {active && (
        <motion.div
          className="absolute inset-0 z-30 pointer-events-none"
          style={{ background: c }}
          initial={{ opacity: 0.65 }}
          animate={{ opacity: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        />
      )}

      <motion.div
        className="relative w-full h-full flex flex-col justify-between"
        initial={false}
        animate={{ opacity: active ? 1 : 0, scale: active ? 1 : 0.96 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
      >
        {/* status bar */}
        <div className="flex items-center justify-between text-white/75" style={{ fontSize: "0.52em" }}>
          <span className="font-mono" style={{ letterSpacing: "0.04em" }}>9:41</span>
          <div className="flex items-center gap-1.5">
            <span className="font-mono" style={{ letterSpacing: "0.04em" }}>5G</span>
            <svg width="1.1em" height="0.7em" viewBox="0 0 14 10" fill="none" aria-hidden="true">
              <rect x="0" y="6" width="2" height="4" rx="0.5" fill="currentColor" opacity="0.9" />
              <rect x="4" y="4" width="2" height="6" rx="0.5" fill="currentColor" opacity="0.9" />
              <rect x="8" y="2" width="2" height="8" rx="0.5" fill="currentColor" opacity="0.9" />
              <rect x="12" y="0" width="2" height="10" rx="0.5" fill="currentColor" opacity="0.9" />
            </svg>
            <span className="relative inline-block" style={{ width: "1.5em", height: "0.75em", border: "1px solid currentColor", borderRadius: "2px" }} aria-hidden="true">
              <span className="absolute" style={{ inset: "1.5px", width: "68%", background: "currentColor", borderRadius: "1px" }} />
              <span className="absolute" style={{ right: "-3px", top: "26%", width: "2px", height: "48%", background: "currentColor", borderRadius: "0 1px 1px 0" }} />
            </span>
          </div>
        </div>

        {/* header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <span className="rounded-full" style={{ width: "0.45em", height: "0.45em", background: c }} />
            <span className="font-mono text-white/90" style={{ fontSize: "0.72em", letterSpacing: "0.1em" }}>WEARSPRAY SENSE</span>
          </div>
          <div className="flex items-center gap-0.5">
            <motion.span className="rounded-full" style={{ width: "0.35em", height: "0.35em", background: "#3ad6a0" }}
              animate={{ opacity: [1, 0.25, 1] }} transition={{ duration: 1.4, repeat: Infinity }} />
            <span className="font-mono text-[#3ad6a0]" style={{ fontSize: "0.6em", letterSpacing: "0.1em" }}>LIVE</span>
          </div>
        </div>

        {/* countdown ring */}
        <div className="flex items-center justify-center">
          <div className="relative flex items-center justify-center" style={{ width: "46%", aspectRatio: "1" }}>
            <div className="absolute inset-0 rounded-full" style={{ background: `conic-gradient(from -90deg, #ff6f61 0%, #00e5ff ${pct}%, rgba(255,255,255,0.12) ${pct}%)` }} />
            <div className="absolute rounded-full" style={{ inset: "13%", background: "#0c0b10" }} />
            <div className="relative text-center leading-none">
              <div className="font-heading text-white" style={{ fontSize: "2em" }}>{mins}</div>
              <div className="font-mono text-white/55" style={{ fontSize: "0.5em", letterSpacing: "0.06em", marginTop: "0.3em" }}>MIN TO REAPPLY</div>
            </div>
          </div>
        </div>

        {/* loaded chip */}
        <div>
          <div className="font-mono text-white/45" style={{ fontSize: "0.55em", letterSpacing: "0.1em" }}>LOADED IN YOUR BAND</div>
          <div className="rounded-xl mt-1 px-2 py-1" style={{ background: "rgba(230,192,104,0.12)", border: "1px solid rgba(230,192,104,0.3)" }}>
            <span className="font-body text-white/90" style={{ fontSize: "0.72em" }}>SPF 50 Sunscreen · Sensitive Skin</span>
          </div>
        </div>

        {/* context chips */}
        <div className="grid grid-cols-3 gap-1">
          {[["UV 8.2", "VERY HIGH"], ["Beach", "LOCATION"], ["Active", "48 MIN"]].map(([a, b]) => (
            <div key={a} className="rounded-xl px-1 py-1 text-center" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}>
              <div className="font-body text-white/90" style={{ fontSize: "0.7em" }}>{a}</div>
              <div className="font-mono text-white/45" style={{ fontSize: "0.5em", letterSpacing: "0.05em", marginTop: "0.15em" }}>{b}</div>
            </div>
          ))}
        </div>

        {/* body-sensor reading row (live) */}
        <div className="rounded-xl px-2 py-1" style={{ background: "rgba(58,214,160,0.08)", border: "1px solid rgba(58,214,160,0.25)" }}>
          <div className="flex items-center justify-between" style={{ marginBottom: "0.3em" }}>
            <div className="flex items-center gap-1">
              <motion.span className="rounded-full" style={{ width: "0.4em", height: "0.4em", background: "#3ad6a0" }}
                animate={{ opacity: [1, 0.2, 1] }} transition={{ duration: 1, repeat: Infinity }} />
              <span className="font-mono text-[#3ad6a0]" style={{ fontSize: "0.55em", letterSpacing: "0.12em" }}>READING BAND</span>
            </div>
            <span className="font-mono text-white/40" style={{ fontSize: "0.5em", letterSpacing: "0.08em" }}>BODY SENSORS</span>
          </div>
          <div className="flex items-center justify-between font-mono text-white/70" style={{ fontSize: "0.62em", letterSpacing: "0.04em" }}>
            <motion.span key={bpm} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>{bpm} BPM</motion.span>
            <span>SWEAT · HIGH</span>
            <span>34°C</span>
          </div>
        </div>

        {/* alert + dispense */}
        <div className="rounded-xl p-1.5" style={{ background: "rgba(230,192,104,0.10)", border: "1px solid rgba(230,192,104,0.25)" }}>
          <div className="font-mono" style={{ color: c, fontSize: "0.55em", letterSpacing: "0.1em" }}>HIGH UV EXPOSURE</div>
          <div className="font-heading text-white" style={{ fontSize: "0.9em", marginTop: "0.1em" }}>Time to refresh your glow</div>
          <motion.div
            className="rounded-full mt-1 text-center"
            style={{ background: "linear-gradient(90deg,#c9a2ff,#00e5ff)", color: "#111", padding: "0.4em 0" }}
            animate={flashDispense
              ? { scale: [1, 1.08, 1], boxShadow: ["0 0 0px rgba(0,229,255,0)", "0 0 28px rgba(0,229,255,0.95)", "0 0 0px rgba(0,229,255,0)"] }
              : { boxShadow: ["0 0 0px rgba(0,229,255,0)", "0 0 10px rgba(0,229,255,0.6)", "0 0 0px rgba(0,229,255,0)"] }}
            transition={flashDispense ? { duration: 0.65, repeat: 1 } : { duration: 1.8, repeat: Infinity }}
          >
            <span className="font-body font-medium" style={{ fontSize: "0.75em" }}>
              {flashDispense ? "Dispensing…" : "Dispense from band"}
            </span>
          </motion.div>
        </div>

        {/* bottom nav */}
        <div className="flex items-center justify-between" style={{ marginTop: "0.2em", paddingTop: "0.45em", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          {["History", "Today", "Profile"].map((label) => {
            const on = label === "Today";
            return (
              <div key={label} className="flex flex-col items-center gap-0.5" style={{ opacity: on ? 1 : 0.4 }}>
                <span className="rounded-full" style={{ width: "0.3em", height: "0.3em", background: on ? c : "rgba(255,255,255,0.5)" }} />
                <span className="font-mono" style={{ fontSize: "0.46em", letterSpacing: "0.06em" }}>{label}</span>
              </div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
}

function ChatConversation({ messages, accent }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (count < messages.length) {
      const t = setTimeout(() => setCount((c) => c + 1), count === 0 ? 700 : 1500);
      return () => clearTimeout(t);
    }
    const reset = setTimeout(() => setCount(0), 4000);
    return () => clearTimeout(reset);
  }, [count, messages.length]);

  return (
    <div className="flex flex-col gap-3 w-full">
      {messages.slice(0, count).map((m, i) => {
        const mine = m.from === "you";
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 14, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            className={`flex ${mine ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[82%] rounded-[20px] px-4 py-2.5 backdrop-blur-md ${
                mine ? "rounded-br-md text-white" : "rounded-bl-md text-white"
              }`}
              style={
                mine
                  ? {
                      background: "linear-gradient(135deg, #ff8fbf 0%, #ff6fa6 55%, #f9709f 100%)",
                      boxShadow: "0 8px 24px rgba(255,111,166,0.35)",
                    }
                  : {
                      background: "linear-gradient(135deg, rgba(46,26,40,0.62) 0%, rgba(28,20,34,0.6) 100%)",
                      border: "1px solid rgba(255,190,214,0.28)",
                      boxShadow: "0 8px 22px rgba(0,0,0,0.35)",
                    }
              }
            >
              <span
                className="block font-mono text-[9px] tracking-[0.28em] uppercase mb-1"
                style={{ color: mine ? "rgba(255,255,255,0.9)" : "#ffc7dd" }}
              >
                {mine ? "You" : "WearSpray"}
              </span>
              <span className="font-body text-sm leading-snug">{m.text}</span>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

function AnimatedIcon({ k, c }) {
  if (k === "Sense") {
    return (
      <motion.div
        animate={{ x: [-1.5, 1.5, -1.5], scale: [1, 1.14, 1] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
      >
        <Activity size={18} strokeWidth={1.9} />
      </motion.div>
    );
  }
  if (k === "Understand") {
    return (
      <div className="relative flex items-center justify-center">
        <motion.span
          className="absolute rounded-full"
          style={{ border: `1px solid ${c}` }}
          initial={{ width: 8, height: 8, opacity: 0.7 }}
          animate={{ width: 32, height: 32, opacity: 0 }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
        />
        <motion.div
          animate={{ scale: [1, 1.12, 1], opacity: [0.85, 1, 0.85] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <Wifi size={18} strokeWidth={1.9} />
        </motion.div>
      </div>
    );
  }
  if (k === "Prompt") {
    return (
      <motion.div
        style={{ transformOrigin: "50% 18%" }}
        animate={{ rotate: [0, -14, 12, -9, 6, -3, 0] }}
        transition={{ duration: 1.1, repeat: Infinity, repeatDelay: 1.2, ease: "easeInOut" }}
      >
        <Bell size={18} strokeWidth={1.9} />
      </motion.div>
    );
  }
  if (k === "Act") {
    return (
      <div className="relative flex items-center justify-center w-[24px] h-[24px]">
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className="absolute rounded-full"
            style={{ background: "#ffffff", width: 3, height: 3, left: `${38 + i * 12}%`, top: 0 }}
            animate={{ y: [-7, 10], opacity: [0, 1, 0] }}
            transition={{ duration: 1, repeat: Infinity, delay: i * 0.45, ease: "easeIn" }}
          />
        ))}
        <Hand size={18} strokeWidth={1.9} />
      </div>
    );
  }
  if (k === "Learn") {
    const sparks = [
      { x: 10, d: 0 }, { x: 18, d: 0.45 }, { x: 25, d: 0.9 }, { x: 14, d: 1.2 },
    ];
    const lines = [
      { x: 8, r: -20 }, { x: 20, r: 0 }, { x: 30, r: 20 },
    ];
    return (
      <div className="relative flex items-center justify-center" style={{ width: 40, height: 40, overflow: "visible" }}>
        {/* rising sparks */}
        {sparks.map((s, i) => (
          <motion.span
            key={"s" + i}
            className="absolute rounded-full"
            style={{ left: s.x, top: 2, width: 3, height: 3, background: c, boxShadow: `0 0 6px ${c}` }}
            animate={{ y: [2, -15], opacity: [0, 1, 0], scale: [0.5, 1, 0.3] }}
            transition={{ duration: 1.7, repeat: Infinity, ease: "easeOut", delay: s.d }}
          />
        ))}
        {/* short thought lines */}
        {lines.map((l, i) => (
          <motion.span
            key={"l" + i}
            className="absolute"
            style={{ left: l.x, top: -6, width: 1.5, height: 9, background: c, borderRadius: 2, transformOrigin: "bottom center", transform: `rotate(${l.r}deg)` }}
            animate={{ opacity: [0, 1, 0], scaleY: [0.4, 1, 0.4] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: i * 0.4 }}
          />
        ))}
        <img loading="lazy"
          src="/icons/learn-head.png"
          alt=""
          aria-hidden="true"
          className="block relative"
          style={{ width: 40, height: 40, objectFit: "contain" }}
        />
      </div>
    );
  }
  return null;
}

const STAGES = [
  {
    n: "01",
    k: "Sense",
    Icon: Activity,
    c: "#00e5ff",
    img: `${A}bz8trw9w_sensor%201%20new%202.jpg`,
    alt: "WearSpray wearable on a woman's wrist with a sensor reading the skin, shown as glowing cyan rings",
    head: "Sense what is happening now.",
    sub: "Biometric and activity signals create real time awareness, while your phone adds location, time and environmental context.",
    pos: "50% 50%",
    imgAspect: 1.624,
    stream: true,
    sensorDots: [
      { x: "57.5%", y: "46.2%", c: "#38b6ff" },
      { x: "60%", y: "45.1%" },
      { x: "62.5%", y: "44%" },
    ],
    callouts: [
      { label: "Push Reservoir", side: "left", x: "55%", y: "19%", lineW: 200 },
      { label: "Delivery Valve", side: "left", x: "44%", y: "35%", lineW: 180 },
      { label: "Biometric + Activity Sensors", side: "right", x: "62.5%", y: "44%", lineW: 110 },
      { label: "Sensor Data → AI App", side: "right", x: "63%", y: "60%", lineW: 110 },
    ],
  },
  {
    n: "02",
    k: "Understand",
    Icon: Wifi,
    c: "#3ad6a0",
    img: `${A}jq435lsz_sensor%20new%20strap2.webp`,
    alt: "The WearSpray band sending its signal to a smartphone showing the live WearSpray Sense interface",
    head: "Turn signals into personal context.",
    sub: "AI combines sensor data with UV exposure, environment, activity, application history, product information and personal preferences to understand what matters right now.",
    pos: "50% 50%",
    imgAspect: 2.0305,
    contain: true,
    noZoom: true,
    flow: true,
    flowColor: "#4fd6ff",
    underGlow: { x: "36%", y: "36%" },
    sensorDots: [
      { x: "37.5%", y: "35.4%", c: "#3ad6a0" },
      { x: "43.5%", y: "33.6%", c: "#4fd6ff" },
      { x: "51.5%", y: "35.9%", c: "#3ad6a0" },
      { x: "57.5%", y: "41.5%", c: "#4fd6ff" },
      { x: "62.5%", y: "50.6%", c: "#3ad6a0" },
    ],
    phone: {
      quad: [
        [0.6605, 0.0345],
        [0.8715, 0.0345],
        [0.6605, 0.9381],
        [0.8715, 0.9381],
      ],
      radius: "40px",
    },
  },
  {
    n: "03",
    k: "Prompt",
    Icon: Bell,
    c: "#e6c068",
    img: `${A}bm6w36ka_SENSOR%204.jpg`,
    alt: "A woman at the ocean holding a phone showing the WearSpray Sense app with a 22-minute reapply timer and a Dispense from band prompt",
    head: "Personal care intelligence that knows when to act.",
    sub: "Tell WearSpray what you are wearing and when you applied it. Over time, the system learns your products, preferences and routines, helping you remember when care may be needed again.",
    sub2: "Because remembering to reapply should not depend on remembering everything yourself.",
    pos: "50% 40%",
    chat: [
      { from: "ws", text: "You usually reapply after about 80 minutes in conditions like these." },
      { from: "you", text: "I am staying out another hour." },
      { from: "ws", text: "Then I would reapply now." },
      { from: "you", text: "Good call." },
      { from: "ws", text: "You are carrying SPF 50. Ready when you are." },
    ],
  },
  {
    n: "04",
    k: "Act",
    Icon: Hand,
    c: "#ff2d78",
    img: "https://static.prod-images.emergentagent.com/jobs/ec62b2d0-2ad9-4890-99e3-2e35ee4d6bac/images/cf90ae578820c8c03f9ccc5ae266121dc910d868b7dcd61ce1ffa79a147570ad.jpeg",
    imgFrom: "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/lbl2ciwn_stream%20act%201.jpg",
    alt: "Lotion dispensing directly from the WearSpray band into a woman's hand at the beach",
    head: "Know what to do. ",
    headAccent: "Then do it instantly.",
    sub: "One press puts the formulation in your palm, giving you immediate access to the care you were just prompted to use.",
    pos: "50% 45%",
  },
  {
    n: "05",
    k: "Learn",
    Icon: Brain,
    c: "#b794f6",
    img: "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/04o6qspr_overshoulder%20close%20up.png",
    alt: "A woman by the lake glancing at the WearSpray Sense app doing a quick check-in, as the system learns her routine",
    head: "Your personal AI skincare consultant. ",
    headAccent: "Smarter with every use.",
    sub: "WearSpray learns from your skin profile, check-ins, routines, location, UV exposure, heat, activity, sensor signals, and the formulation you choose to carry. Over time, it recognizes your patterns and delivers increasingly relevant guidance for sunscreen reapplication, moisturizer, or whatever personal care solution you have selected.",
    sub2: "The intelligence is in your hand. The power to act is on your wrist.",
    pos: "42% 52%",
  },
];

function StageText({ s, inView }) {
  return (
    <>
      <motion.div
        className="flex items-center gap-2 sm:gap-4 mb-4"
        initial={{ opacity: 0, y: 16 }}
        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <span className="shrink-0 font-mono text-xs sm:text-sm tracking-[0.3em]" style={{ color: s.c }}>{s.n}</span>
        <span className="shrink-0 h-px w-6 sm:w-10" style={{ background: s.c }} />
        <div className="shrink-0 rounded-full border flex items-center justify-center w-11 h-11 sm:w-16 sm:h-16" style={{ borderColor: s.c, color: s.c }}>
          <div style={{ transform: "scale(1.15)" }}>
            <AnimatedIcon k={s.k} c={s.c} />
          </div>
        </div>
        <span
          className="min-w-0 truncate font-mono uppercase font-medium text-xl sm:text-3xl md:text-5xl tracking-[0.1em] sm:tracking-[0.22em]"
          style={{ color: s.c }}
        >
          {s.k}
        </span>
      </motion.div>

      <motion.h2
        className="font-heading font-light tracking-tighter leading-[1.04] text-white text-4xl sm:text-5xl lg:text-6xl"
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
      >
        {s.head}
        {s.headAccent && (
          <>
            <br />
            <span style={{ color: s.c }}>{s.headAccent}</span>
          </>
        )}
      </motion.h2>

      <motion.p
        className="font-body text-zinc-300 leading-relaxed text-lg md:text-xl mt-6"
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
      >
        {s.sub}
      </motion.p>
      {s.sub2 && (
        <motion.p
          className="font-body text-white text-base md:text-lg mt-4 leading-relaxed italic max-w-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.32, ease: "easeOut" }}
        >
          {s.sub2}
        </motion.p>
      )}
    </>
  );
}

function Stage({ s, index }) {
  const ref = useRef(null);
  const layerRef = useRef(null);
  const inView = useInView(ref, { amount: 0.4 });
  const [layer, setLayer] = useState({ w: 0, h: 0 });
  // parse "X% Y%" objectPosition into numbers so the photo-anchored overlay
  // layer can replicate object-fit:cover's exact anchoring math for any pos,
  // not just the centered 50%/50% default.
  const [posX, posY] = (s.pos || "50% 50%").split(" ").map((v) => parseFloat(v));
  useEffect(() => {
    if (!s.phone || !s.phone.quad) return;
    const el = layerRef.current;
    if (!el) return;
    const update = () => setLayer({ w: el.offsetWidth, h: el.offsetHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [s]);
  const [phoneActive, setPhoneActive] = useState(false);
  useEffect(() => {
    if (!s.phone || !s.phone.quad) return;
    if (inView) {
      const t = setTimeout(() => setPhoneActive(true), 1400);
      return () => clearTimeout(t);
    }
    // debounce the deactivation so a brief intersection flicker (e.g. while
    // other page assets are still settling) doesn't reset the reveal
    const t2 = setTimeout(() => setPhoneActive(false), 400);
    return () => clearTimeout(t2);
  }, [inView, s]);
  return (
    <section
      ref={ref}
      className={`relative w-full bg-black overflow-hidden ${index > 0 ? "mt-8" : ""}`}
      data-testid={`loop-stage-${s.n}`}
    >
      {/* Text lives above the image now, in normal document flow, so it never
          has to fight a busy photo for legibility. */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-14 md:pt-16 pb-10 md:pb-12" data-testid={`stage-text-${s.n}`}>
        <div className="max-w-xl">
          <StageText s={s} inView={inView} />
        </div>
      </div>

      <div className="relative h-[62vh] min-h-[440px] w-full overflow-hidden">
      {s.contain && (
        <div
          className="absolute inset-0 w-full h-full"
          aria-hidden="true"
          style={{
            backgroundImage: `url(${s.img})`,
            backgroundSize: "cover",
            backgroundPosition: s.pos || "50% 50%",
            filter: "blur(60px) brightness(0.42) saturate(1.15)",
            transform: "scale(1.15)",
          }}
        />
      )}
      {s.noZoom ? (
        // Stages with a pixel-mapped phone overlay (fixed phone.quad) render the
        // photo completely still: the Ken Burns settle-in zoom below animates
        // <img loading="lazy"> scale independently of the static overlay layer, which made the
        // mapped phone UI visibly drift/re-drift off the photo every time the
        // section re-entered view.
        <img loading="lazy"
          src={s.img}
          alt={s.alt}
          className={`absolute inset-0 w-full h-full ${s.contain ? "object-contain" : "object-cover"}`}
          style={{ objectPosition: s.pos }}
        />
      ) : (
        <motion.img
          src={s.img}
          alt={s.alt}
          className={`absolute inset-0 w-full h-full ${s.contain ? "object-contain" : "object-cover"}`}
          style={{ objectPosition: s.pos }}
          initial={{ scale: s.contain ? 1 : 1.08 }}
          animate={inView ? { scale: 1 } : { scale: s.contain ? 1 : 1.08 }}
          transition={{ duration: 6, ease: "easeOut" }}
        />
      )}

      {s.imgFrom && (
        <motion.img
          src={s.imgFrom}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover z-[1]"
          style={{ objectPosition: s.pos }}
          initial={{ opacity: 1, scale: 1 }}
          animate={inView ? { opacity: [1, 1, 0, 0, 1], scale: [1, 1.06, 1.08, 1.02, 1] } : { opacity: 1, scale: 1 }}
          transition={{ duration: 8, times: [0, 0.35, 0.5, 0.85, 1], ease: "easeInOut", repeat: Infinity }}
        />
      )}

      {/* Photo-anchored overlay layer: locked to the image aspect so anchors stay on the same
          spot of the photo regardless of viewport crop */}
      <div
        ref={layerRef}
        className="absolute pointer-events-none"
        style={
          s.imgAspect
            ? {
                left: `${posX}%`,
                top: `${posY}%`,
                transform: `translate(-${posX}%, -${posY}%)`,
                width: s.contain ? `min(100vw, calc(max(62vh, 440px) * ${s.imgAspect}))` : `max(100vw, calc(max(62vh, 440px) * ${s.imgAspect}))`,
                height: s.contain ? `min(max(62vh, 440px), calc(100vw / ${s.imgAspect}))` : `max(max(62vh, 440px), calc(100vw / ${s.imgAspect}))`,
              }
            : { left: 0, top: 0, width: "100%", height: "100%" }
        }
      >

      {s.phone && s.phone.quad && layer.w > 0 && (() => {
        const DW = 380, DH = 800; // design size of the phone screen (kept crisp, warped by matrix)
        const dst = s.phone.quad.map(([fx, fy]) => [fx * layer.w, fy * layer.h]);
        return (
          <div
            className="absolute z-[8] hidden md:block overflow-hidden"
            style={{
              left: 0, top: 0, width: DW, height: DH,
              transform: matrix3dFor(DW, DH, dst),
              transformOrigin: "0 0",
              borderRadius: s.phone.radius || "22px",
              boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06), inset 0 0 60px rgba(0,0,0,0.5)",
            }}
            data-testid={`phone-screen-${s.n}`}
          >
            <PhoneScreen c={s.c} active={phoneActive} />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: "linear-gradient(118deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.03) 20%, rgba(255,255,255,0) 42%)" }}
              aria-hidden="true"
            />
          </div>
        );
      })()}

      {s.phone && !s.phone.quad && (
        <div
          className="absolute z-[8] hidden md:block overflow-hidden"
          style={{
            left: s.phone.left, top: s.phone.top, width: s.phone.width, height: s.phone.height,
            transform: s.phone.transform || `rotate(${s.phone.rotate || 0}deg)`,
            transformOrigin: s.phone.origin || "center",
            borderRadius: s.phone.radius || "9% / 5%",
            background: "#08080c", padding: "4% 4.5%", boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
          }}
          data-testid={`phone-screen-${s.n}`}
        >
          <PhoneScreen c={s.c} />
        </div>
      )}

      {s.sensorDots && (
        <div className="absolute inset-0 z-[4] pointer-events-none" data-testid={`sensor-dots-${s.n}`} aria-hidden="true">
          {s.sensorDots.map((d, k) => {
            const col = d.c || "#3ad6a0";
            return (
              <span key={k} className="absolute" style={{ left: d.x, top: d.y }} aria-hidden="true">
                {/* wavelength rings radiating off the dot */}
                {[0, 1].map((r) => (
                  <span
                    key={r}
                    className="absolute rounded-full border"
                    style={{
                      width: 40, height: 40, left: -20, top: -20,
                      borderColor: col,
                      boxShadow: `0 0 14px ${col}66`,
                      animation: `senseWave 2.4s ease-out ${k * 0.3 + r * 1.2}s infinite`,
                      opacity: 0,
                    }}
                  />
                ))}
                {/* the dot */}
                <span
                  className="absolute rounded-full"
                  style={{
                    left: -7, top: -7, width: 14, height: 14,
                    background: col,
                    boxShadow: `0 0 14px ${col}, 0 0 28px ${col}cc`,
                    animation: `senseCore 1.9s ease-in-out ${k * 0.3}s infinite`,
                  }}
                />
              </span>
            );
          })}
        </div>
      )}

      {s.stream && (
        <div className="absolute inset-0 z-[5] pointer-events-none" data-testid={`sensor-stream-${s.n}`} aria-hidden="true">
          {[0, 1, 2, 3].map((k) => (
            <span
              key={k}
              className="absolute rounded-full"
              style={{
                left: "55%", top: "50%", width: 5, height: 5, marginLeft: -2.5, marginTop: -2.5,
                background: "#3ad6a0",
                boxShadow: "0 0 8px #3ad6a0",
                animation: `sensorStream 2.4s ease-in ${k * 0.6}s infinite`,
                opacity: 0,
              }}
            />
          ))}
        </div>
      )}

      {s.callouts && (
        <div className="absolute inset-0 z-[7] pointer-events-none hidden md:block" data-testid={`callouts-${s.n}`} aria-hidden="true">
          {s.callouts.map((c, k) => {
            const base = 0.3 + k * 0.25;
            const line = (
              <span
                style={{
                  width: c.lineW, height: 1, background: "rgba(255,255,255,0.9)",
                  boxShadow: "0 0 6px rgba(0,229,255,0.6)",
                  transformOrigin: c.side === "left" ? "right center" : "left center",
                  animation: `growX 0.55s ease-out ${base}s both`,
                }}
              />
            );
            return (
              <div key={c.label}>
                <span
                  className="absolute rounded-full"
                  style={{
                    left: c.x, top: c.y, width: 12, height: 12, marginLeft: -6, marginTop: -6,
                    background: "#fff", boxShadow: "0 0 12px #fff, 0 0 22px #00e5ff",
                    animation: `senseCore 1.8s ease-in-out ${k * 0.3}s infinite`,
                  }}
                />
                <div
                  className="absolute flex items-center gap-4"
                  style={{
                    left: c.x, top: c.y,
                    transform: c.side === "left" ? "translate(-100%, -50%)" : "translateY(-50%)",
                    paddingRight: c.side === "left" ? 12 : 0,
                    paddingLeft: c.side === "right" ? 12 : 0,
                  }}
                >
                  {c.side === "right" && line}
                  <span
                    className="font-heading font-light tracking-tight text-white text-2xl md:text-3xl whitespace-nowrap"
                    style={{ textShadow: "0 2px 12px rgba(0,0,0,0.85)", animation: `calloutIn 0.5s ease-out ${base + 0.45}s both` }}
                  >
                    {c.label}
                  </span>
                  {c.side === "left" && line}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {s.underGlow && (() => {
        const fc = s.flowColor || s.c;
        return (
        <div
          className="absolute z-[4] pointer-events-none hidden md:block"
          style={{ left: s.underGlow.x, top: s.underGlow.y, transform: "translate(-50%, -50%)" }}
          data-testid={`under-glow-${s.n}`}
          aria-hidden="true"
        >
          <span
            className="absolute rounded-full"
            style={{
              width: 150, height: 150, left: -75, top: -75,
              background: `radial-gradient(circle, ${fc}77 0%, ${fc}00 70%)`,
              filter: "blur(3px)",
              animation: "sensorGlow 2.2s ease-in-out infinite",
            }}
          />
          <span
            className="absolute rounded-full"
            style={{ width: 8, height: 8, left: -19, top: -3, background: fc, boxShadow: `0 0 14px ${fc}, 0 0 26px ${fc}`, animation: "senseCore 1.6s ease-in-out infinite" }}
          />
          <span
            className="absolute rounded-full"
            style={{ width: 8, height: 8, left: 13, top: -3, background: fc, boxShadow: `0 0 14px ${fc}, 0 0 26px ${fc}`, animation: "senseCore 1.6s ease-in-out 0.8s infinite" }}
          />
        </div>
        );
      })()}

      {s.flow && (
        <div className="absolute inset-0 z-[5] pointer-events-none hidden md:block" data-testid={`data-flow-${s.n}`} aria-hidden="true">
          {[0, 1, 2, 3, 4, 5, 6].map((k) => {
            const fc = s.flowColor || "#8af3ff";
            const big = k % 3 === 0;
            return (
              <span
                key={k}
                className="absolute rounded-full"
                style={{
                  width: big ? 9 : 5, height: big ? 9 : 5,
                  background: "#ffffff",
                  boxShadow: `0 0 10px ${fc}, 0 0 22px ${fc}`,
                  animation: `dataFlow02 2.2s linear ${k * 0.32}s infinite`,
                  opacity: 0,
                }}
              />
            );
          })}
        </div>
      )}

      {s.screen && (
        <div className="absolute inset-0 z-[6] pointer-events-none" data-testid={`screen-fx-${s.n}`} aria-hidden="true">
          <span
            className="absolute rounded-full"
            style={{
              left: s.screen.live.x, top: s.screen.live.y,
              width: 10, height: 10, marginLeft: -5, marginTop: -5,
              background: "#3ad6a0", boxShadow: "0 0 12px #3ad6a0, 0 0 22px #3ad6a0aa",
              animation: "senseCore 1.4s ease-in-out infinite",
            }}
          />
          {(s.screen.metrics || []).map((m, k) => (
            <span
              key={k}
              className="absolute rounded-full"
              style={{
                left: m.x, top: m.y,
                width: 66, height: 66, marginLeft: -33, marginTop: -33,
                border: "2px solid #8af3ff",
                boxShadow: "0 0 16px #8af3ff88, inset 0 0 12px #8af3ff44",
                animation: `metricScan 4s ease-in-out ${k * 1.33}s infinite`,
                opacity: 0,
              }}
            />
          ))}
        </div>
      )}

      {s.labels && s.labels.map((l, k) => (
        <div
          key={k}
          className="absolute z-[7] pointer-events-none flex items-center gap-1.5 rounded-full border px-2.5 py-1"
          style={{
            left: l.x, top: l.y, transform: "translate(-50%, -50%)",
            borderColor: `${l.color}66`,
            background: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
          }}
          data-testid={`stage-label-${s.n}-${k}`}
        >
          <span
            className="rounded-full shrink-0"
            style={{ width: 6, height: 6, background: l.color, boxShadow: `0 0 8px ${l.color}`, animation: "senseCore 1.4s ease-in-out infinite" }}
          />
          <span className="font-mono uppercase tracking-[0.14em] text-white whitespace-nowrap" style={{ fontSize: "10.5px", textShadow: "0 1px 4px rgba(0,0,0,0.9)" }}>
            {l.text}
          </span>
        </div>
      ))}

      {s.sensor && (
        <div
          className="absolute z-[5] pointer-events-none"
          style={{ left: s.sensor.x, top: s.sensor.y, transform: "translate(-50%, -50%)" }}
          data-testid={`sense-fx-${s.n}`}
          aria-hidden="true"
        >
          {[0, 1, 2, 3, 4].map((k) => (
            <span
              key={k}
              className="absolute rounded-full border-2"
              style={{
                width: 190,
                height: 190,
                left: -95,
                top: -95,
                borderColor: s.c,
                boxShadow: `0 0 30px ${s.c}66, inset 0 0 22px ${s.c}40`,
                animation: `senseRing 3.4s ease-out ${k * 0.7}s infinite`,
                opacity: 0,
              }}
            />
          ))}
          <span
            className="absolute rounded-full"
            style={{
              width: 22,
              height: 22,
              left: -11,
              top: -11,
              background: s.c,
              boxShadow: `0 0 26px ${s.c}, 0 0 52px ${s.c}cc`,
              animation: "senseCore 2s ease-in-out infinite",
            }}
          />
        </div>
      )}


      </div>

      {s.phoneMock && (
        <>
          {/* full, upright WearSpray Sense phone — placed where the data arc lands, on the shared dark scene */}
          <motion.div
            className="absolute z-[8] hidden md:block"
            style={{ right: "27%", top: "50%", height: "min(82vh, 800px)", aspectRatio: "740 / 1572" }}
            initial={{ opacity: 0, y: "-46%", scale: 0.96 }}
            animate={inView ? { opacity: 1, y: "-50%", scale: 1 } : { opacity: 0, y: "-46%", scale: 0.96 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            data-testid={`phone-mock-${s.n}`}
          >
            {/* connection glow where the arc meets the phone's left edge */}
            <div
              className="absolute pointer-events-none"
              style={{ left: "-22%", top: "18%", width: "45%", height: "45%", background: "radial-gradient(circle, rgba(56,214,255,0.5) 0%, rgba(56,214,255,0) 70%)", filter: "blur(6px)" }}
              aria-hidden="true"
            />
            <div
              className="relative w-full h-full overflow-hidden"
              style={{
                background: "#050506",
                borderRadius: "13% / 6.2%",
                padding: "2.6%",
                boxShadow: "0 40px 90px rgba(0,0,0,0.7), inset 0 0 0 1px rgba(255,255,255,0.08)",
              }}
            >
              <div className="relative w-full h-full overflow-hidden" style={{ borderRadius: "10.5% / 5%" }}>
                <PhoneScreen c={s.c} />
              </div>
              <div
                className="absolute left-1/2 -translate-x-1/2"
                style={{ top: "3.4%", width: "30%", height: "2.4%", minHeight: "16px", background: "#050506", borderRadius: "999px" }}
                aria-hidden="true"
              />
            </div>
          </motion.div>
        </>
      )}

      {s.chat && (
        <div
          className="hidden lg:block absolute right-6 md:right-12 top-1/2 -translate-y-1/2 z-10 w-[380px]"
          data-testid={`chat-${s.n}`}
        >
          {inView && <ChatConversation messages={s.chat} accent={s.c} />}
        </div>
      )}

      </div>
    </section>
  );
}

export function ClosedLoopHero() {
  return (
    <div data-testid="closed-loop-hero">
      {/* Intro line */}
      <section className="bg-black border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-12 pb-8 md:pt-14 md:pb-10 text-center">
          <p className="font-mono text-[11px] md:text-xs tracking-[0.3em] uppercase text-accent">The Closed-Loop System</p>
        </div>
      </section>

      {STAGES.map((s, i) => (
        <Stage key={s.n} s={s} index={i} />
      ))}
    </div>
  );
}
